/** "Manage game data" overlay: what's installed, re-import, erase. */

import { idbfsDbName } from '../config';
import { readRegistry, clearRegistry } from '../registry';
import { downloadBlob, exportPersistedSaveArchive } from '../saves';
import { deleteEverything, getMeta, storageEstimate, type InstallMeta } from '../store';
import { $, bytes, confirmDanger, toast } from './dom';

export interface ManagePanel {
  open(): void;
  close(): void;
}

export function mountManage(onReimport: () => void): ManagePanel {
  const overlay = $('#overlay-manage');
  const table = $('#manage-table');

  const render = async () => {
    const meta = await getMeta<InstallMeta>('install');
    const est = await storageEstimate();
    const rows: [string, string][] = [];
    if (meta) {
      rows.push(['imported', new Date(meta.installedAt).toLocaleString()]);
      rows.push(['source', meta.source]);
      rows.push(['files', String(meta.fileCount)]);
      rows.push(['size', bytes(meta.totalBytes)]);
      rows.push(['archives', meta.dats.join(', ') || '—']);
      rows.push(['1.02 patch', meta.hasPatch ? 'present' : 'MISSING']);
    } else {
      rows.push(['status', 'nothing imported yet']);
    }
    if (est) {
      rows.push(['browser storage used', bytes(est.usage)]);
      rows.push(['browser storage quota', bytes(est.quota)]);
    }
    const persisted = await navigator.storage?.persisted?.().catch(() => false);
    rows.push(['eviction-protected', persisted ? 'yes' : 'no']);

    table.replaceChildren();
    for (const [k, v] of rows) {
      const tr = document.createElement('tr');
      const a = document.createElement('td');
      a.textContent = k;
      const b = document.createElement('td');
      b.textContent = v;
      tr.append(a, b);
      table.appendChild(tr);
    }
  };

  $('#btn-manage-close').addEventListener('click', () => overlay.classList.remove('open'));
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) overlay.classList.remove('open');
  });
  $('#btn-reimport').addEventListener('click', () => {
    overlay.classList.remove('open');
    onReimport();
  });
  $('#btn-export-saves').addEventListener('click', async (event) => {
    const button = event.currentTarget as HTMLButtonElement;
    const label = button.textContent;
    button.disabled = true;
    button.textContent = 'Preparing save ZIP…';
    try {
      const result = await exportPersistedSaveArchive();
      downloadBlob(result.blob, result.filename);
      toast(`Exported ${result.slotCount} save slot${result.slotCount === 1 ? '' : 's'} (${bytes(result.blob.size)}).`);
    } catch (error) {
      toast(error instanceof Error ? error.message : String(error), 'warn', 6000);
    } finally {
      button.disabled = false;
      button.textContent = label;
    }
  });
  $('#btn-reset').addEventListener('click', async () => {
    if (!(await confirmDanger('Erase the imported game data and ALL saves stored in this browser?'))) return;
    if (!(await confirmDanger('Last chance. Exported .zip files on disk are unaffected, but browser saves are gone for good.')))
      return;
    const reg = readRegistry();
    const vaultNames = reg.map((e) => e.dbName);
    if (!vaultNames.length) vaultNames.push('f2ce-shell');
    const idbfsNames = [idbfsDbName('fo1'), idbfsDbName('fo2')];
    await deleteEverything(vaultNames, idbfsNames);
    clearRegistry();
    location.reload();
  });

  return {
    open() {
      void render();
      overlay.classList.add('open');
    },
    close() {
      overlay.classList.remove('open');
    },
  };
}
